import CalendarClaimHeader from '@gik/calendar/components/ClaimEvent/CalendarClaimHeader';
import { ICalendarEntry, ICalendarEvent } from '@gik/calendar/models/Calendar';
import { CalendarEventTypeIds } from '@gik/calendar/models/CalendarEventTypes';
import { IWordpressCalendarEventType } from '@gik/core/models/wordpress/WordpressCalendarEventType';
import { useBemCN } from '@gik/core/utils/bemBlock';
import noop from '@gik/core/utils/noop';
import { renderPortal } from '@gik/core/utils/RenderPortal';
import { Button, SvgIcon } from '@gik/ui';
import React from 'react';
import { PaymentConfirmationValues } from '../../types';
import PaymentConfirmation from './PaymentConfirmation';

export interface ClaimPaymentConfirmationProps extends React.HTMLAttributes<HTMLDivElement> {
  values: PaymentConfirmationValues;
  productName: string;
  shipping?: boolean;
  purchasedClaimedItem: boolean;
  purchasedDeletedItem: boolean;
  eventDate: string;
  entry: ICalendarEntry;
  event: ICalendarEvent;
  eventType: IWordpressCalendarEventType;
  buttonsPortal?: () => HTMLElement;
  onDone?: () => void;
}

export default function ClaimPaymentConfirmation({
  className,
  values,
  productName,
  shipping,
  eventType,
  eventDate,
  purchasedClaimedItem,
  purchasedDeletedItem,
  entry,
  event,
  buttonsPortal,
  onDone = noop,
  ...otherProps
}: ClaimPaymentConfirmationProps): React.ReactElement {
  const renderFullfillmentDescription = fulfillmentTimeString => (
    <>
      <>
        &nbsp;to help with <strong>{entry.typeId !== CalendarEventTypeIds.Other ? entry.typeName : entry.title}</strong>{' '}
        on <strong>{eventDate}</strong>.
      </>
      {purchasedClaimedItem && (
        <>
          <br />
          <br />
          Someone claimed the {eventDate} {eventType.name} request just before you did. Your gift card has still been
          sent, however it will not be visible on the calendar. If you wish to request a refund, please contact support
          within 24 hours.
        </>
      )}
      {purchasedDeletedItem && (
        <>
          <br />
          <br />
          It looks like the {eventDate} {eventType.name} request was deleted by an organizer of this page. Your gift
          card has still been sent, however it will not be visible on the calendar. If you wish to request a refund,
          please contact support within 24 hours.
        </>
      )}
      &nbsp;Gift Cards are typically fulfilled and sent to the recipient in {fulfillmentTimeString}.{' '}
    </>
  );

  const bem = useBemCN('claim-payment-confirmation');
  const header = <CalendarClaimHeader {...bem('header')} entry={entry} eventType={eventType} event={event} />;
  const successIcon = (
    <div {...bem('check')}>
      <SvgIcon name="CheckOutline" size="3xl" />
    </div>
  );

  const _buttons = (
    <div className="gik-form__actions-new gik-form__actions-new--centered">
      <Button onClick={onDone}>Done</Button>
    </div>
  );

  return (
    <>
      <PaymentConfirmation
        header={header}
        successIcon={successIcon}
        values={values}
        productName={productName}
        shipping={shipping}
        renderFullfillmentDescription={renderFullfillmentDescription}
        {...bem(undefined, undefined, className)}
        {...otherProps}
      />
      {renderPortal(_buttons, buttonsPortal)}
    </>
  );
}
